/**
 * Purchase receipt hooks (#3033, T12c2): the paged receipt history of a purchase order and the three keyed commands (receive goods,
 * decide held units, close short) on the staff-api `purchase-receipts` routes (T12b1).
 *
 * Contract notes
 *  - The server is the only evaluator. Quantities, status and completion are shown as the server sent them; the one cost-bearing
 *    part of the history (`costs`) is requested of a reader with `reports.view_financial` and is stripped from what the screens get
 *    otherwise, so a downgrade never leaves a stale cost on screen or in the cache.
 *  - Query keys carry the owner (tenant, user, session family) and the cost visibility, so another principal, an earlier login of
 *    the same user or the same user without the financial grant neither finds nor shares a cached answer.
 *  - Every read and write is bound to the owner (tenant, user, session family, generation, lock), the permissions it needs and the
 *    purchase order it began under. They are checked from the live auth store just before it is sent and again when the answer
 *    arrives; a `Fence` breaks for good on any change, so A, B, A and lose-then-regain a permission never make an answer current.
 *    A late answer never reaches the screen, the cache or a notification (`LateResultError`). A write the server CONFIRMED still marks
 *    the order, parts, stock and history queries stale (refetching only while the sending session is unchanged), even if its screen
 *    was closed or its purchase order replaced. That is cache-only: a screen that is gone, or no longer on that order, is told
 *    nothing (`canceled`: no callback, no state, no notification), whatever the reason for it going.
 *  - Receive, hold decisions and close-short take a caller-owned `Idempotency-Key`. The exact request (target, body, key) is
 *    written to a bounded, owner-scoped `sessionStorage` journal and READ BACK before it is sent; a storage failure blocks the
 *    dispatch (no memory fallback). After an ambiguous outcome the same request stays held, across an unmount or reload, and is only
 *    ever sent again, unchanged, by an explicit retry. A fresh key is minted only for a new operation after a definite refusal, a
 *    success or a knowingly confirmed discard. The page also remembers each unresolved request per owner: if the stored record is
 *    erased, truncated or replaced meanwhile, a new request for the same intent is refused, and an explicit retry first writes the
 *    exact remembered key and body back (read back identical) before sending it. Memory never replaces storage as a precondition.
 *    A refused-storage state is cleared by an explicit `recheckStorage` or by the next explicit write that is kept; never by a send.
 *  - Nothing is ever sent on mount.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import {
  LateResultError,
  OwnerChangedError,
  RECEIPT_READ_PERMISSION,
  RECEIPT_WRITE_PERMISSION,
  ReceivingAccessError,
  UnexpectedResponseError,
  ownerEquals,
  ownerId,
  readReceivingAccess,
  takeFence,
  useReceivingAccess,
  type Fence,
  type Outcome,
  type ReceivingAccess,
  type ReceivingOwner,
  type TargetBinding,
} from '@/features/purchase-orders/receiving/receivingSession';
import {
  beginFlight,
  dropCorruptJournal,
  endFlight,
  getJournalView,
  probeJournalStorage,
  putEntry,
  recordAttempt,
  removeEntry,
  restoreEntry,
  slotOf,
  useJournalView,
  type JournalEntry,
  type JournalStatus,
  type PutStatus,
} from '@/features/purchase-orders/receiving/receivingJournal';
import { isDefiniteRefusal } from '@/features/purchase-orders/receiving/receivingErrors';
import type {
  CloseShortBody,
  CloseShortEventView,
  CloseShortResult,
  HoldResolutionView,
  OperationBodyMap,
  OperationKind,
  OperationResultMap,
  ReceiptHistoryPage,
  ReceiptLineCosts,
  ReceiptLineView,
  ReceiptView,
  ReceiveGoodsBody,
  ReceiveGoodsResult,
  ResolveHoldBody,
  ResolveHoldResult,
  UserRef,
} from '@/features/purchase-orders/receiving/receivingTypes';

export type {
  CloseShortBody,
  CloseShortEventView,
  CloseShortResult,
  HoldResolutionView,
  OperationKind,
  ReceiptHistoryPage,
  ReceiptLineCosts,
  ReceiptLineView,
  ReceiptView,
  ReceiveGoodsBody,
  ReceiveGoodsResult,
  ResolveHoldBody,
  ResolveHoldResult,
} from '@/features/purchase-orders/receiving/receivingTypes';

const INVENTORY = '/inventory';

// ---------------------------------------------------------------------------
// Keys and the cache guard
// ---------------------------------------------------------------------------

export interface HistoryPageRequest {
  limit: number;
  offset: number;
}

/** One segment names the owner: tenant, user and session family (the legacy session has its own family name). */
export const purchaseReceiptKeys = {
  root: ['purchase-receipts'] as const,
  all: (owner: ReceivingOwner) => [...purchaseReceiptKeys.root, ownerId(owner)] as const,
  history: (owner: ReceivingOwner, purchaseOrderId: string, page: HistoryPageRequest, financial: boolean) =>
    [...purchaseReceiptKeys.all(owner), 'history', purchaseOrderId, page.limit, page.offset, financial ? 'financial' : 'units'] as const,
  historyOf: (owner: ReceivingOwner, purchaseOrderId: string) => [...purchaseReceiptKeys.all(owner), 'history', purchaseOrderId] as const,
};

/**
 * Drops every receipt query of another owner as soon as the owner changes, and every cost-bearing answer of this owner as soon as
 * the financial grant is lost, so a cached answer is not merely unreachable (the keys already differ) but gone.
 */
export function useReceiptCacheGuard(): void {
  const client = useQueryClient();
  const { owner, canFinancial } = useReceivingAccess();
  const own = owner ? ownerId(owner) : 'none';
  useEffect(() => {
    client.removeQueries({ queryKey: purchaseReceiptKeys.root, predicate: (query) => query.queryKey[1] !== own });
  }, [client, own]);
  useEffect(() => {
    if (own === 'none' || canFinancial) return;
    client.removeQueries({ queryKey: purchaseReceiptKeys.root, predicate: (query) => query.queryKey[1] === own && query.queryKey.includes('financial') });
  }, [client, own, canFinancial]);
}

// ---------------------------------------------------------------------------
// Target binding
// ---------------------------------------------------------------------------

export interface BoundTarget {
  /** The target of this render. */
  readonly render: TargetBinding;
  /** The target now, `null` once the hook's component is gone. */
  readonly live: () => TargetBinding;
}

/** The purchase order a hook works on, with a generation that moves on every change and a mounted flag. */
function useBoundTarget(target: string | null): BoundTarget {
  const ref = useRef<TargetBinding>({ target, generation: 0 });
  if (ref.current.target !== target) ref.current = { target, generation: ref.current.generation + 1 };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const render = ref.current;
  return { render, live: () => (mounted.current ? ref.current : { target: null, generation: ref.current.generation }) };
}

async function settle<T>(call: () => Promise<T>): Promise<Outcome<T>> {
  try {
    return { ok: true, value: await call() };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * One read of the session that offered it. The owner, permissions and target are checked from the live store just before it leaves
 * and again when the answer arrives; an answer that outlived any of them is carried in a `LateResultError`, never returned.
 */
export async function runOwned<T>(access: Pick<ReceivingAccess, 'owner' | 'epoch'>, permissions: readonly string[], bound: BoundTarget, call: () => Promise<T>): Promise<T> {
  const fence = takeFence(access, permissions, bound);
  const outcome = await settle(call);
  const holds = fence.holds();
  fence.release();
  if (!holds) throw new LateResultError(outcome);
  if (!outcome.ok) throw outcome.error;
  return outcome.value;
}

// ---------------------------------------------------------------------------
// Defensive parsing of answers
// ---------------------------------------------------------------------------

type Rec = Record<string, unknown>;
const rec = (value: unknown): Rec => (typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Rec) : {});
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const strOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const int = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const bool = (value: unknown): boolean => value === true;

const parseUser = (value: unknown): UserRef => ({ id: str(rec(value)['id']), name: str(rec(value)['name']) });

function parseCosts(value: unknown): ReceiptLineCosts | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const costs = rec(value);
  return {
    unitPriceCents: int(costs['unitPriceCents']),
    discountPercent: int(costs['discountPercent']),
    layers: arr(costs['layers']).map((layer) => ({
      id: str(rec(layer)['id']),
      quantity: int(rec(layer)['quantity']),
      unitCostCents: strOrNull(rec(layer)['unitCostCents']),
      costStatus: str(rec(layer)['costStatus']),
    })),
  };
}

function parseResolution(value: unknown): HoldResolutionView {
  const row = rec(value);
  return {
    id: str(row['id']),
    decision: row['decision'] === 'reject' ? 'reject' : 'accept',
    quantity: int(row['quantity']),
    rejectReason: strOrNull(row['rejectReason']),
    note: strOrNull(row['note']),
    decidedBy: parseUser(row['decidedBy']),
    decidedAt: str(row['decidedAt']),
  };
}

function parseReceiptLine(value: unknown): ReceiptLineView {
  const row = rec(value);
  const line: ReceiptLineView = {
    id: str(row['id']),
    purchaseOrderLineId: str(row['purchaseOrderLineId']),
    lineNumber: int(row['lineNumber']),
    partId: str(row['partId']),
    partSku: str(row['partSku']),
    partName: str(row['partName']),
    quantityArrived: int(row['quantityArrived']),
    quantityAccepted: int(row['quantityAccepted']),
    quantityRejected: int(row['quantityRejected']),
    quantityHeld: int(row['quantityHeld']),
    quantityHeldInitial: int(row['quantityHeldInitial']),
    rejectReason: strOrNull(row['rejectReason']),
    note: strOrNull(row['note']),
    // Anything but an explicit `true` is "no quality check recorded", never a checked zero.
    qualityObserved: row['qualityObserved'] === true,
    qualityObservedAt: strOrNull(row['qualityObservedAt']),
    holdResolutions: arr(row['holdResolutions']).map(parseResolution),
  };
  const costs = parseCosts(row['costs']);
  if (costs) line.costs = costs;
  return line;
}

function parseReceipt(value: unknown): ReceiptView {
  const row = rec(value);
  return {
    id: str(row['id']),
    receiptNumber: str(row['receiptNumber']),
    purchaseOrderId: str(row['purchaseOrderId']),
    receivedAt: str(row['receivedAt']),
    receivedBy: parseUser(row['receivedBy']),
    deliveryNoteReference: strOrNull(row['deliveryNoteReference']),
    note: strOrNull(row['note']),
    lines: arr(row['lines']).map(parseReceiptLine),
  };
}

function parseCloseShortEvent(value: unknown): CloseShortEventView {
  const row = rec(value);
  return {
    id: str(row['id']),
    actor: parseUser(row['actor']),
    reason: str(row['reason']),
    effectiveAt: str(row['effectiveAt']),
    lines: arr(row['lines']).map((line) => ({ purchaseOrderLineId: str(rec(line)['purchaseOrderLineId']), quantityCancelled: int(rec(line)['quantityCancelled']) })),
  };
}

/** An answer of the history route for exactly `purchaseOrderId`; anything else is an `UnexpectedResponseError`. */
export function parseReceiptHistory(raw: unknown, purchaseOrderId: string): ReceiptHistoryPage {
  const page = rec(raw);
  if (page['purchaseOrderId'] !== purchaseOrderId || !Array.isArray(page['items'])) throw new UnexpectedResponseError();
  const pagination = rec(page['pagination']);
  const closeShort = rec(page['closeShort']);
  const items = page['items'].map(parseReceipt);
  if (items.some((item) => item.purchaseOrderId !== purchaseOrderId)) throw new UnexpectedResponseError();
  return {
    purchaseOrderId,
    items,
    pagination: { limit: int(pagination['limit']), offset: int(pagination['offset']), total: int(pagination['total']), hasMore: bool(pagination['hasMore']) },
    closeShort: { total: int(closeShort['total']), items: arr(closeShort['items']).map(parseCloseShortEvent) },
    costsVisible: bool(page['costsVisible']),
  };
}

/** The history as a reader without the financial grant may see it: no order line price, discount or cost layer, ever. */
export function withoutCosts(page: ReceiptHistoryPage): ReceiptHistoryPage {
  return {
    ...page,
    costsVisible: false,
    items: page.items.map((receipt) => ({
      ...receipt,
      lines: receipt.lines.map((line) => {
        const { costs: _costs, ...rest } = line;
        return rest;
      }),
    })),
  };
}

function parseReceive(raw: unknown): ReceiveGoodsResult {
  const row = rec(raw);
  return {
    replayed: bool(row['replayed']),
    receiptId: str(row['receiptId']),
    receiptNumber: str(row['receiptNumber']),
    purchaseOrderId: str(row['purchaseOrderId']),
    purchaseOrderStatus: str(row['purchaseOrderStatus']),
    fullyReceived: bool(row['fullyReceived']),
    lines: arr(row['lines']).map((line) => {
      const value = rec(line);
      return {
        lineId: str(value['lineId']),
        quantityOrdered: int(value['quantityOrdered']),
        quantityArrived: int(value['quantityArrived']),
        quantityAccepted: int(value['quantityAccepted']),
        quantityRejected: int(value['quantityRejected']),
        quantityHeld: int(value['quantityHeld']),
        qualityObserved: value['qualityObserved'] === true,
        quantityReceivedTotal: int(value['quantityReceivedTotal']),
      };
    }),
  };
}

function parseHold(raw: unknown): ResolveHoldResult {
  const row = rec(raw);
  const line = rec(row['receiptLine']);
  return {
    replayed: bool(row['replayed']),
    purchaseOrderId: str(row['purchaseOrderId']),
    receiptLineId: str(row['receiptLineId']),
    decision: row['decision'] === 'reject' ? 'reject' : 'accept',
    quantity: int(row['quantity']),
    receiptLine: { accepted: int(line['accepted']), rejected: int(line['rejected']), held: int(line['held']) },
    purchaseOrderStatus: str(row['purchaseOrderStatus']),
    fullyReceived: bool(row['fullyReceived']),
  };
}

function parseCloseShort(raw: unknown): CloseShortResult {
  const row = rec(raw);
  return {
    replayed: bool(row['replayed']),
    purchaseOrderId: str(row['purchaseOrderId']),
    purchaseOrderStatus: str(row['purchaseOrderStatus']),
    fullyReceived: bool(row['fullyReceived']),
    reason: str(row['reason']),
    lines: arr(row['lines']).map((line) => {
      const value = rec(line);
      return { lineId: str(value['lineId']), cancelled: int(value['cancelled']), heldUnresolved: int(value['heldUnresolved']), lineComplete: bool(value['lineComplete']) };
    }),
  };
}

const parsers: { [K in OperationKind]: (raw: unknown) => OperationResultMap[K] } = {
  receive: parseReceive,
  resolve_hold: parseHold,
  close_short: parseCloseShort,
};

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

/**
 * One page of the receipt history of a purchase order: `inventory.read`. Costs arrive only for a reader who also holds
 * `reports.view_financial` (the server decides; the portal additionally strips them when the grant is gone now).
 */
export function useReceiptHistory(purchaseOrderId: string | null, page: HistoryPageRequest, options: { enabled?: boolean } = {}) {
  const access = useReceivingAccess();
  const bound = useBoundTarget(purchaseOrderId);
  useReceiptCacheGuard();
  const { owner, canFinancial } = access;
  return useQuery({
    queryKey: owner && purchaseOrderId ? purchaseReceiptKeys.history(owner, purchaseOrderId, page, canFinancial) : [...purchaseReceiptKeys.root, 'none'],
    queryFn: () =>
      runOwned(access, [RECEIPT_READ_PERMISSION], bound, async () => {
        const target = purchaseOrderId ?? '';
        const raw = await api.get<unknown>(`${INVENTORY}/purchase-orders/${encodeURIComponent(target)}/receipts`, {
          params: { limit: page.limit, offset: page.offset },
        });
        return parseReceiptHistory(raw, target);
      }),
    select: (data) => (canFinancial ? data : withoutCosts(data)),
    enabled: owner !== null && access.canRead && purchaseOrderId !== null && (options.enabled ?? true),
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Keyed commands
// ---------------------------------------------------------------------------

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** `changed`: the stored record of this request is not the one this page wrote; nothing is sent until the user abandons it. */
export type BlockedReason = 'pending' | 'corrupt' | 'storage' | 'full' | 'missing' | 'changed';

const blockedBy = (status: Exclude<PutStatus, 'stored'>): BlockedReason =>
  status === 'conflict' ? 'changed' : status === 'corrupt' ? 'corrupt' : status === 'full' ? 'full' : 'storage';

export type SubmitResult<K extends OperationKind = OperationKind> =
  | { status: 'success'; kind: K; result: OperationResultMap[K] }
  /** The server read the request and wrote nothing: the operation is released. `attempts` > 1 means a retry was refused. */
  | { status: 'refused'; error: unknown; attempts: number }
  /** The write may or may not have happened: the same request is held. */
  | { status: 'uncertain'; error: unknown }
  /** A request of this slot is already on its way. */
  | { status: 'busy' }
  /** Nothing was sent: an earlier request is held, the journal is damaged or the browser would not keep it. */
  | { status: 'blocked'; reason: BlockedReason }
  /**
   * Nothing may be reported to this screen: the session, tenant, user, generation, lock, a permission or the purchase order changed,
   * before the request was sent (then nothing was) or while it was on its way (its answer was kept for its owner's journal only).
   */
  | { status: 'canceled'; error?: unknown };

export function operationUrl(kind: OperationKind, target: string): string {
  const id = encodeURIComponent(target);
  if (kind === 'receive') return `${INVENTORY}/purchase-orders/${id}/receipts`;
  if (kind === 'resolve_hold') return `${INVENTORY}/purchase-receipt-lines/${id}/resolve-hold`;
  return `${INVENTORY}/purchase-orders/${id}/close-short`;
}

/**
 * The cache after a write the server confirmed, whether or not the screen that sent it is still there. It only ever MARKS queries
 * stale: no answer, no payload and nothing of the old session is written to any cache.
 *  - The session that sent it is unchanged (owner, generation, lock, rights): active queries refetch under it now.
 *  - Anything else (another owner, a lock, a permission lost and regained): the order, parts and stock queries are marked stale
 *    without a refetch (`refetchType: 'none'`), so the next screen that mounts reads fresh data under its own credentials, and no
 *    request is made on behalf of a session that is gone. The owner-scoped history is touched only when its owner is the live one
 *    (every other owner's queries are removed by the cache guard anyway).
 */
function reconcileConfirmedWrite(client: QueryClient, owner: ReceivingOwner, purchaseOrderId: string, unchanged: boolean): void {
  const live = readReceivingAccess();
  const refetchType = unchanged && live.active ? 'active' : 'none';
  if (ownerEquals(live.owner, owner)) {
    void client.invalidateQueries({ queryKey: purchaseReceiptKeys.historyOf(owner, purchaseOrderId), refetchType });
  }
  void client.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.all, refetchType });
  void client.invalidateQueries({ queryKey: queryKeys.inventory.parts.all, refetchType });
  void client.invalidateQueries({ queryKey: queryKeys.inventory.stockLevels(), refetchType });
}

export interface ReceivingOperations {
  /** Unresolved requests of this purchase order: held after an uncertain outcome, found again after a reload, or prepared. */
  entries: readonly JournalEntry[];
  /** `unavailable`: the browser keeps nothing, so nothing is sent. `corrupt`: a record could not be read, nothing new starts. */
  journalStatus: JournalStatus;
  /** A request of this slot is on its way, also one started by a screen that has since unmounted. */
  isPending: (slot: string) => boolean;
  /** The last answer of this hook's own attempt of a slot; `null` for a request found again after a reload. */
  errorOf: (slot: string) => unknown;
  canReceive: boolean;
  /** Starts a new receipt with a new key. */
  receive: (body: ReceiveGoodsBody) => Promise<SubmitResult<'receive'>>;
  /** Starts a new hold decision on one receipt line. */
  resolveHold: (receiptLineId: string, body: ResolveHoldBody) => Promise<SubmitResult<'resolve_hold'>>;
  closeShort: (body: CloseShortBody) => Promise<SubmitResult<'close_short'>>;
  /** Sends a held request again: the same target, body and key, only for its own session. */
  retry: (entry: JournalEntry) => Promise<SubmitResult>;
  /** Gives a held request up. Only an explicit, confirmed user action may call this. */
  discard: (entry: JournalEntry) => void;
  /** The user knowingly discards a damaged journal record (it was never used). */
  discardDamaged: () => void;
  /**
   * Explicit recovery after the browser refused storage: probes that a record can be written and read back again, without a reload.
   * It sends nothing and creates no request; the next receive still persists and reads back its own entry first. True when cleared.
   */
  recheckStorage: () => boolean;
}

/**
 * The commands of one purchase order. A request belongs to the session that started it: another tenant, user, session family or
 * generation neither sees nor sends it, a late answer of an old session is not reported to the current one, and a client refusal
 * before a retry never resolves or deletes the unknown outcome of the earlier attempt.
 */
export function useReceivingOperations(purchaseOrderId: string | null): ReceivingOperations {
  const access = useReceivingAccess();
  const view = useJournalView(access.owner);
  const client = useQueryClient();
  const bound = useBoundTarget(purchaseOrderId);
  useReceiptCacheGuard();
  const ownerKey = access.owner ? ownerId(access.owner) : 'none';
  const [failures, setFailures] = useState<Record<string, { owner: string; error: unknown }>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const entries = view.entries.filter((entry) => entry.purchaseOrderId === purchaseOrderId);
  const report = (slot: string, error: unknown) => {
    if (mounted.current) setFailures((current) => ({ ...current, [slot]: { owner: ownerKey, error } }));
  };
  const clearReport = (slot: string) => {
    if (mounted.current) setFailures((current) => (slot in current ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== slot)) : current));
  };

  const dispatch = async (entry: JournalEntry, fence: Fence): Promise<SubmitResult> => {
    const { owner, slot, kind } = entry;
    const { key } = entry.request;
    // Only a request that storage holds right now, exactly as remembered, may leave.
    if (entry.durability !== 'kept') return { status: 'blocked', reason: 'storage' };
    if (!beginFlight(owner, slot, key)) return { status: 'busy' };
    recordAttempt(owner, slot, key);
    const attempts = entry.attempts + 1;
    clearReport(slot);
    const raw = await settle(() => api.post<unknown>(operationUrl(kind, entry.target), entry.request.body, { headers: { 'Idempotency-Key': key } }));
    const current = fence.holds();
    let result: SubmitResult;
    if (raw.ok) {
      // The server decided: the operation is over whoever is looking, and the caches learn it even if the screen is gone.
      removeEntry(owner, slot, key);
      reconcileConfirmedWrite(client, owner, entry.purchaseOrderId, current || fence.authHolds());
      result = current ? ({ status: 'success', kind, result: parsers[kind](raw.value) } as SubmitResult) : { status: 'canceled' };
    } else if (isDefiniteRefusal(raw.error)) {
      removeEntry(owner, slot, key);
      result = current ? { status: 'refused', error: raw.error, attempts } : { status: 'canceled' };
    } else {
      // Unknown: the entry stays, for its owner.
      result = current ? { status: 'uncertain', error: raw.error } : { status: 'canceled' };
    }
    endFlight(owner, slot, key);
    if (result.status === 'refused' || result.status === 'uncertain') report(slot, result.error);
    return result;
  };

  const start = async <K extends OperationKind>(kind: K, target: string, body: OperationBodyMap[K]): Promise<SubmitResult<K>> => {
    if (purchaseOrderId === null) return { status: 'canceled', error: new OwnerChangedError() };
    let fence: Fence;
    try {
      fence = takeFence(access, [RECEIPT_WRITE_PERMISSION], bound);
    } catch (error) {
      return error instanceof ReceivingAccessError ? { status: 'refused', error, attempts: 1 } : { status: 'canceled', error };
    }
    try {
      const slot = slotOf(kind, target);
      const current = getJournalView(fence.owner);
      if (current.status === 'corrupt') return { status: 'blocked', reason: 'corrupt' };
      // `unavailable` is not a verdict: the write below is the probe. It must be persisted and read back, or nothing is sent.
      if (current.inFlight.has(slot)) return { status: 'busy' };
      if (current.entries.some((candidate) => candidate.slot === slot)) return { status: 'blocked', reason: 'pending' };

      const request = { key: newIdempotencyKey(), body };
      const put = putEntry(fence.owner, { kind, purchaseOrderId, target, request });
      if (put === 'conflict') return { status: 'blocked', reason: 'pending' };
      if (put !== 'stored') return { status: 'blocked', reason: blockedBy(put) };
      // What is dispatched is what storage holds now.
      const stored = getJournalView(fence.owner).entries.find((candidate) => candidate.slot === slot && candidate.request.key === request.key && candidate.durability === 'kept');
      if (!stored) return { status: 'blocked', reason: 'storage' };
      return (await dispatch(stored, fence)) as SubmitResult<K>;
    } finally {
      fence.release();
    }
  };

  const retry = async (entry: JournalEntry): Promise<SubmitResult> => {
    let fence: Fence;
    try {
      fence = takeFence(access, [RECEIPT_WRITE_PERMISSION], bound);
    } catch (error) {
      // The earlier outcome stays unknown and stays held.
      return { status: 'canceled', error };
    }
    try {
      if (!ownerEquals(entry.owner, fence.owner) || entry.purchaseOrderId !== purchaseOrderId) return { status: 'canceled', error: new OwnerChangedError() };
      const current = getJournalView(fence.owner);
      if (current.status === 'corrupt') return { status: 'blocked', reason: 'corrupt' };
      if (current.inFlight.has(entry.slot)) return { status: 'busy' };
      // Exactly the request storage holds now, under the key it was written with.
      const stored = current.entries.find((candidate) => candidate.slot === entry.slot && candidate.request.key === entry.request.key);
      if (!stored) return { status: 'blocked', reason: 'missing' };
      if (stored.durability === 'replaced') return { status: 'blocked', reason: 'changed' };
      if (stored.durability === 'lost') {
        // The stored record is gone: write the exact remembered request back and read it back before it may be sent again.
        const restored = restoreEntry(fence.owner, stored.slot, stored.request.key);
        if (restored !== 'stored') return { status: 'blocked', reason: blockedBy(restored) };
        const back = getJournalView(fence.owner).entries.find((candidate) => candidate.slot === stored.slot && candidate.request.key === stored.request.key);
        if (!back || back.durability !== 'kept') return { status: 'blocked', reason: 'storage' };
        return await dispatch(back, fence);
      }
      return await dispatch(stored, fence);
    } finally {
      fence.release();
    }
  };

  const discard = (entry: JournalEntry) => {
    let fence: Fence;
    try {
      fence = takeFence(access, [], bound);
    } catch {
      return;
    }
    try {
      if (!ownerEquals(entry.owner, fence.owner) || getJournalView(fence.owner).inFlight.has(entry.slot)) return;
      removeEntry(fence.owner, entry.slot, entry.request.key);
      clearReport(entry.slot);
    } finally {
      fence.release();
    }
  };

  const discardDamaged = () => {
    try {
      const fence = takeFence(access, [], bound);
      fence.release();
      dropCorruptJournal(fence.owner);
    } catch {
      // Not this owner's to discard.
    }
  };

  const recheckStorage = (): boolean => {
    try {
      const fence = takeFence(access, [], bound);
      fence.release();
      return probeJournalStorage(fence.owner);
    } catch {
      return false;
    }
  };

  return {
    entries,
    journalStatus: view.status,
    isPending: (slot) => view.inFlight.has(slot),
    errorOf: (slot) => {
      const failure = failures[slot];
      return failure && failure.owner === ownerKey ? failure.error : null;
    },
    canReceive: access.canReceive,
    receive: (body) => start('receive', purchaseOrderId ?? '', body),
    resolveHold: (receiptLineId, body) => start('resolve_hold', receiptLineId, body),
    closeShort: (body) => start('close_short', purchaseOrderId ?? '', body),
    retry,
    discard,
    discardDamaged,
    recheckStorage,
  };
}
